import type { ElkNode } from "elkjs";
import type { RuleGraphPosition } from "./layout";

export interface LayoutBox {
  id: string;
  width: number;
  height: number;
  header?: boolean;
  layer?: "FIRST" | "LAST";
}

export interface LayoutConnection {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

export interface GraphBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GraphGeometry {
  bounds: GraphBounds;
  positionsByNodeId: Map<string, RuleGraphPosition>;
  pathsByEdgeId: Map<string, string>;
}

export const pointsToPath = (points: RuleGraphPosition[]): string =>
  points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x},${point.y}`)
    .join(" ");

/** Size-aware general geometry. The executor is a worker in the browser. */
export async function layoutGeneralGraph(
  boxes: LayoutBox[],
  connections: LayoutConnection[],
  execute: (graph: ElkNode) => Promise<ElkNode>,
  presentation: "canvas" | "row" = "canvas"
): Promise<GraphGeometry> {
  if (boxes.length === 0) {
    return {
      positionsByNodeId: new Map(),
      pathsByEdgeId: new Map(),
      bounds: { x: 0, y: 0, width: 0, height: 0 },
    };
  }
  const ordered = [...boxes].sort((a, b) => a.id.localeCompare(b.id));
  const ports = new Map<string, string>();
  const result = await execute({
    id: "rule-graph",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.spacing.nodeNode": "36",
      "elk.layered.spacing.nodeNodeBetweenLayers":
        presentation === "row" ? "28" : "64",
      "elk.padding":
        presentation === "row"
          ? "[top=0,left=22,bottom=0,right=0]"
          : "[top=32,left=32,bottom=32,right=32]",
      "elk.randomSeed": "1",
    },
    children: ordered.map((box, index) => ({
      ...box,
      layoutOptions: {
        "elk.portConstraints": "FIXED_POS",
        ...(box.layer
          ? { "elk.layered.layering.layerConstraint": box.layer }
          : {}),
      },
      ports: [
        { name: "flow-target", side: "WEST", x: 0, y: box.height / 2 },
        { name: "flow-source", side: "EAST", x: box.width, y: box.height / 2 },
        {
          name: "fallback-target",
          side: "NORTH",
          x: box.header ? 5 : box.width / 2,
          y: 0,
        },
        {
          name: "fallback-source",
          side: "SOUTH",
          x: box.header ? 5 : box.width / 2,
          y: box.height,
        },
      ].map(({ name, side, x, y }) => {
        const id = `port-${index}-${name}`;
        ports.set(JSON.stringify([box.id, name]), id);
        return {
          id,
          x,
          y,
          width: 0,
          height: 0,
          layoutOptions: { "elk.port.side": side },
        };
      }),
    })),
    edges: [...connections]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((edge) => ({
        id: edge.id,
        sources: [
          ports.get(
            JSON.stringify([edge.source, edge.sourceHandle ?? "flow-source"])
          )!,
        ],
        targets: [
          ports.get(
            JSON.stringify([edge.target, edge.targetHandle ?? "flow-target"])
          )!,
        ],
      })),
  });
  const positionsByNodeId = new Map<string, RuleGraphPosition>();
  for (const node of result.children ?? []) {
    if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) {
      throw new Error("Graph layout returned invalid coordinates");
    }
    positionsByNodeId.set(node.id, { x: node.x!, y: node.y! });
  }
  const pathsByEdgeId = new Map<string, string>();
  for (const edge of result.edges ?? []) {
    const sections = edge.sections ?? [];
    if (sections.length === 0) {
      throw new Error("Graph layout returned an unrouted connection");
    }
    pathsByEdgeId.set(
      edge.id,
      sections
        .map((section) =>
          pointsToPath([
            section.startPoint,
            ...(section.bendPoints ?? []),
            section.endPoint,
          ])
        )
        .join(" ")
    );
  }
  if (
    positionsByNodeId.size !== boxes.length ||
    pathsByEdgeId.size !== connections.length
  ) {
    throw new Error("Graph layout is incomplete");
  }
  return {
    positionsByNodeId,
    pathsByEdgeId,
    bounds: graphGeometryBounds(boxes, positionsByNodeId, pathsByEdgeId),
  };
}

/** Include routed bends as well as cards when fitting the canvas. */
export function graphGeometryBounds(
  boxes: LayoutBox[],
  positions: Map<string, RuleGraphPosition>,
  paths: Map<string, string>
): GraphBounds {
  const points: RuleGraphPosition[] = [];
  for (const box of boxes) {
    const p = positions.get(box.id);
    if (!p) {
      throw new Error("Missing graph node position");
    }
    points.push(p, { x: p.x + box.width, y: p.y + box.height });
  }
  for (const path of paths.values()) {
    for (const point of path.matchAll(/[ML] ([\d.e+-]+),([\d.e+-]+)/g)) {
      points.push({ x: Number(point[1]), y: Number(point[2]) });
    }
  }
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  if (points.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    throw new Error("Invalid graph coordinates");
  }
  let x = Infinity,
    y = Infinity,
    right = -Infinity,
    bottom = -Infinity;
  for (const point of points) {
    x = Math.min(x, point.x);
    y = Math.min(y, point.y);
    right = Math.max(right, point.x);
    bottom = Math.max(bottom, point.y);
  }
  return { x, y, width: right - x, height: bottom - y };
}
